import { useState, useEffect } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { apiService } from '@/services/apiService';
import { formatDate } from '@/lib/utils';
import { Shield, ShieldAlert, ShieldCheck, Activity, Server, HardDrive, Wifi, AlertTriangle, CheckCircle2, XCircle, Filter, Bug } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const levelConfig = {
  critical: { label: 'CRÍTICO', className: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle, color: 'text-red-400' },
  warning: { label: 'ALERTA', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertTriangle, color: 'text-amber-400' },
  info: { label: 'INFO', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: CheckCircle2, color: 'text-blue-400' },
};

const categoryConfig = {
  intrusion: { label: 'Intrusão', icon: ShieldAlert },
  resource: { label: 'Recursos', icon: Activity },
  firewall: { label: 'Firewall', icon: Shield },
  update: { label: 'Atualização', icon: Server },
  backup: { label: 'Backup', icon: HardDrive },
  disk: { label: 'Disco', icon: HardDrive },
  ssl: { label: 'SSL', icon: Wifi },
};

const severityConfig = {
  CRITICO: { className: 'bg-red-500/10 text-red-400 border-red-500/20', color: 'text-red-400' },
  ALTO: { className: 'bg-orange-500/10 text-orange-400 border-orange-500/20', color: 'text-orange-400' },
  MEDIO: { className: 'bg-amber-500/10 text-amber-400 border-amber-500/20', color: 'text-amber-400' },
  BAIXO: { className: 'bg-blue-500/10 text-blue-400 border-blue-500/20', color: 'text-blue-400' },
};

export default function GuardsPage() {
  const [logs, setLogs] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [stats, setStats] = useState(null);
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [logsData, statsData, agentsData, vulnsData] = await Promise.all([
        apiService.getSecurityLogs(),
        apiService.getSecuritySummary(),
        apiService.getAgents(),
        apiService.getVulnerabilities(true),
      ]);
      setLogs(logsData);
      setStats(statsData);
      setAgent(agentsData.find(a => a.id === 'sys'));
      setVulnerabilities(vulnsData);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter((log) => {
    if (filterLevel !== 'all' && log.level !== filterLevel) return false;
    if (filterCategory !== 'all' && log.category !== filterCategory) return false;
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-96">Carregando...</div>;
  }

  return (
    <div data-testid="guards-page">
      <PageHeader
        title="Segurança"
        description="Monitoramento e logs de segurança do VPS - Agente Sys"
      />

      {/* Agent + Stats Overview */}
      {agent && (
        <div className="mb-6 rounded-xl border border-red-500/20 bg-gradient-to-br from-red-500/5 to-transparent backdrop-blur-sm p-4" data-testid="security-agent-card">
          <div className="flex items-center gap-4 mb-4">
            {/* Avatar aumentado: h-16 w-16 */}
            <div
              className="h-16 w-16 rounded-lg bg-cover bg-center border-2 flex-shrink-0"
              style={{ backgroundImage: `url(${agent.avatar})`, borderColor: agent.color }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-xl font-bold" style={{ color: agent.color }}>{agent.name}</h3>
                <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-400 uppercase">
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  ONLINE
                </div>
              </div>
              <p className="text-xs text-muted-foreground uppercase">{agent.specialty}</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono">{agent.default_model}</span>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Principais - 4 Cards Essenciais */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="security-stats">
          <StatCard
            label="Alertas (24h)"
            value={stats.total_events}
            icon={Activity}
            color="blue"
            testId="stat-events"
          />
          <StatCard
            label="Processos"
            value={stats.processos_monitorados}
            icon={Server}
            color="emerald"
            testId="stat-processos"
          />
          <StatCard
            label="Ataques (24h)"
            value={stats.tentativas_ataque_24h}
            icon={ShieldAlert}
            color={stats.tentativas_ataque_24h > 0 ? 'red' : 'emerald'}
            testId="stat-tentativas"
          />
          <StatCard
            label="Vulns Ativas"
            value={stats.vulnerabilidades_ativas}
            icon={Bug}
            color={stats.vulnerabilidades_ativas > 0 ? 'amber' : 'emerald'}
            testId="stat-vulnerabilidades"
          />
        </div>
      )}

      {/* Tabs: Logs + Vulnerabilidades */}
      <Tabs defaultValue="logs" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Logs ({filteredLogs.length})
          </TabsTrigger>
          <TabsTrigger value="vulnerabilities" className="flex items-center gap-2">
            <Bug className="h-4 w-4" />
            Vulnerabilidades ({vulnerabilities.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="logs">
          {/* Filters */}
          <div className="mb-4 flex flex-wrap gap-3 items-center">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="w-32 sm:w-40" data-testid="filter-level">
                <SelectValue placeholder="Nível" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Níveis</SelectItem>
                <SelectItem value="critical">Crítico</SelectItem>
                <SelectItem value="warning">Alerta</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-32 sm:w-40" data-testid="filter-category">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="intrusion">Intrusão</SelectItem>
                <SelectItem value="firewall">Firewall</SelectItem>
                <SelectItem value="resource">Recursos</SelectItem>
                <SelectItem value="disk">Disco</SelectItem>
                <SelectItem value="update">Atualização</SelectItem>
                <SelectItem value="backup">Backup</SelectItem>
                <SelectItem value="ssl">SSL</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Security Logs */}
          <div className="space-y-2" data-testid="security-logs">
            {filteredLogs.map((log) => {
              const level = levelConfig[log.level] || levelConfig.info;
              const LevelIcon = level.icon;
              const cat = categoryConfig[log.category] || { label: log.category, icon: Shield };
              const CatIcon = cat.icon;

              return (
                <div
                  key={log.id}
                  className={cn(
                    'rounded-lg border bg-card/40 backdrop-blur-sm p-3 hover:border-primary/30 transition-all',
                    log.level === 'critical' ? 'border-red-500/20' : 'border-border/50'
                  )}
                  data-testid="security-log-item"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <LevelIcon className={cn('h-4 w-4 flex-shrink-0', level.color)} />
                      <span className={cn(
                        'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9px] font-bold tracking-widest border',
                        level.className
                      )}>
                        {level.label}
                      </span>
                      <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/30 rounded px-1.5 py-0.5">
                        <CatIcon className="h-3 w-3" />
                        <span className="uppercase font-bold hidden sm:inline">{cat.label}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-muted-foreground">{formatDate(log.timestamp)}</span>
                      {log.resolved ? (
                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                      ) : (
                        <ShieldAlert className="h-3.5 w-3.5 text-red-400 animate-pulse" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-foreground/90 ml-6">{log.message}</p>
                  <div className="flex items-center gap-3 mt-2 ml-6 text-[10px] text-muted-foreground">
                    <span className="font-mono uppercase">{log.source}</span>
                    {!log.resolved && (
                      <span className="text-red-400 font-bold uppercase">Pendente</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredLogs.length === 0 && (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground rounded-lg border border-border/50 bg-card/40">
              <ShieldCheck className="h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm">Nenhum log encontrado</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="vulnerabilities">
          {/* Vulnerabilities List */}
          <div className="space-y-3" data-testid="vulnerabilities-list">
            {vulnerabilities.map((vuln) => {
              const severity = severityConfig[vuln.severidade] || severityConfig.MEDIO;
              
              return (
                <div
                  key={vuln.id}
                  className={cn(
                    'rounded-lg border bg-card/40 backdrop-blur-sm p-4 hover:border-primary/30 transition-all',
                    vuln.severidade === 'CRITICO' ? 'border-red-500/30' : 'border-border/50'
                  )}
                  data-testid="vulnerability-item"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <Bug className={cn('h-5 w-5 flex-shrink-0 mt-0.5', severity.color)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={cn(
                          'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9px] font-bold tracking-widest border',
                          severity.className
                        )}>
                          {vuln.severidade}
                        </span>
                        {vuln.cve && (
                          <a 
                            href={`https://nvd.nist.gov/vuln/detail/${vuln.cve}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-mono text-primary hover:underline"
                          >
                            {vuln.cve}
                          </a>
                        )}
                        <span className="text-[10px] text-muted-foreground bg-muted/30 px-1.5 py-0.5 rounded uppercase">
                          {vuln.componente}
                        </span>
                      </div>
                      <h4 className="text-sm font-semibold mb-1">{vuln.tipo}</h4>
                      <p className="text-xs text-muted-foreground mb-2">{vuln.descricao}</p>
                      <div className="flex flex-wrap items-center gap-4 text-[10px] text-muted-foreground">
                        <span>Versão: <span className="font-mono">{vuln.versao_afetada}</span></span>
                        <span>Descoberta: {formatDate(vuln.descoberta_em)}</span>
                        {vuln.corrigido ? (
                          <span className="text-emerald-400 font-bold uppercase">✓ Corrigido</span>
                        ) : (
                          <span className="text-red-400 font-bold uppercase animate-pulse">⚠ Não Corrigido</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {vulnerabilities.length === 0 && (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground rounded-lg border border-border/50 bg-card/40">
              <ShieldCheck className="h-10 w-10 mb-2 opacity-50 text-emerald-500" />
              <p className="text-sm">Nenhuma vulnerabilidade ativa</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  const colorMap = {
    blue: 'border-blue-500/20 bg-blue-500/5 text-blue-400',
    red: 'border-red-500/20 bg-red-500/5 text-red-400',
    amber: 'border-amber-500/20 bg-amber-500/5 text-amber-400',
    emerald: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-400',
  };
  const iconColor = {
    blue: 'text-blue-500',
    red: 'text-red-500',
    amber: 'text-amber-500',
    emerald: 'text-emerald-500',
  };

  return (
    <div className={cn('rounded-lg border p-3 backdrop-blur-sm', colorMap[color])} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-muted-foreground truncate">{label}</p>
          <p className="mt-1 font-heading text-xl sm:text-2xl font-bold">{value}</p>
        </div>
        <Icon className={cn('h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0', iconColor[color])} />
      </div>
    </div>
  );
}
